import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { supabase } from '../lib/supabase'

interface HeartRateStats {
  average: number
  stdDev: number
  min: number
  max: number
  sampleCount: number
}

interface UseHeartRateStatsReturn {
  stats: HeartRateStats | null
  loading: boolean
  error: string | null
  refreshStats: () => Promise<void>
}

export const useHeartRateStats = (workoutId: string): UseHeartRateStatsReturn => {
  const heartRateStatsQuery = useQuery<HeartRateStats | null>({
    queryKey: ['heart-rate-stats', workoutId],
    enabled: Boolean(workoutId),
    queryFn: async () => {
      const { data: metrics, error: metricsError } = await supabase
        .from('workout_metrics')
        .select('values')
        .eq('workout_id', workoutId)
        .eq('metric_name', 'heart_rate')
        .single()

      if (metricsError) {
        if ((metricsError as any).code === 'PGRST116') {
          return null
        }
        throw new Error(metricsError.message)
      }

      if (!metrics || !metrics.values || metrics.values.length === 0) {
        return null
      }

      const hrValues = metrics.values.filter((val: number | null) => val !== null) as number[]

      if (hrValues.length === 0) {
        return null
      }

      const n = hrValues.length
      const mean = hrValues.reduce((sum, hr) => sum + hr, 0) / n
      const squaredDiffs = hrValues.map((hr) => (hr - mean) ** 2)
      const variance = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / n
      const stdDev = Math.sqrt(variance)
      const min = Math.min(...hrValues)
      const max = Math.max(...hrValues)

      return {
        average: mean,
        stdDev: stdDev,
        min: min,
        max: max,
        sampleCount: n,
      }
    },
  })

  const refreshStats = useCallback(async () => {
    await heartRateStatsQuery.refetch()
  }, [heartRateStatsQuery])

  return {
    stats: heartRateStatsQuery.data ?? null,
    loading: heartRateStatsQuery.isLoading || heartRateStatsQuery.isFetching,
    error: heartRateStatsQuery.error ? (heartRateStatsQuery.error as Error).message : null,
    refreshStats,
  }
}
